// API service for connecting to FastAPI backend
// Uses relative /api path by default so frontend and backend can share the same domain
import type { ChatMessage, User, Attachment, Notification } from '../types';
import { toast } from '../components/UI/Toast';
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

export class AuthenticationError extends Error {
  status: number;
  constructor(message: string, status: number = 401) {
    super(message);
    this.name = 'AuthenticationError';
    this.status = status;
  }
}

interface ApiResponse<T> {
  data?: T;
  error?: string;
}

class ApiService {
  private token: string | null = null;
  private userCache: any = null;

  constructor() {
    this.token = localStorage.getItem('planner_token');
    const savedUser = localStorage.getItem('planner_user');
    if (savedUser) {
      try {
        this.userCache = JSON.parse(savedUser);
      } catch (error) {
        console.error('Failed to parse cached user data:', error);
        localStorage.removeItem('planner_user');
      }
    }
  }

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    return headers;
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      if (response.status === 401) {
        console.warn('API: Unauthorized response (401), clearing token and session');
        this.token = null;
        this.userCache = null;
        localStorage.removeItem('planner_token');
        localStorage.removeItem('planner_user');
        localStorage.removeItem('planner_projects');
        localStorage.removeItem('planner_issues');
        localStorage.removeItem('planner_sprints');
        
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        throw new AuthenticationError('Session expired. Please log in again.', 401);
      }

      const errorData = await response.json().catch(() => ({ detail: 'Network error' }));
      // Handle Pydantic validation errors where detail is an array of objects
      let message: string;
      if (Array.isArray(errorData.detail)) {
        message = errorData.detail
          .map((err: any) => typeof err === 'string' ? err : (err.msg || JSON.stringify(err)))
          .join('; ');
      } else if (typeof errorData.detail === 'string') {
        message = errorData.detail;
      } else if (errorData.detail && typeof errorData.detail === 'object') {
        message = errorData.detail.msg || JSON.stringify(errorData.detail);
      } else {
        message = `HTTP ${response.status}: ${response.statusText}`;
      }
      toast.error(message);
      throw new Error(message);
    }
    return response.json();
  }

  // Authentication
  async login(email: string, password: string) {
    console.log('API: Attempting login for:', email);
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ email, password }),
    });

    const data = await this.handleResponse<{
      access_token: string;
      token_type: string;
      user: any;
    }>(response);

    console.log('API: Login successful, caching data');
    this.token = data.access_token;
    this.userCache = data.user;

    // Store in localStorage (handled by useAuth hook)
    console.log('API: Authentication data ready for caching');

    return data;
  }

  async sendOtp(email: string) {
    console.log('API: Sending OTP to:', email);
    const response = await fetch(`${API_BASE_URL}/auth/otp/send`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ email }),
    });
    return this.handleResponse<{ message: string, otp_test_fallback?: string }>(response);
  }

  async otpLogin(email: string, otp: string) {
    console.log('API: Verifying OTP for:', email);
    const response = await fetch(`${API_BASE_URL}/auth/otp/login`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ email, otp }),
    });

    const data = await this.handleResponse<{
      access_token: string;
      token_type: string;
      user: any;
    }>(response);

    console.log('API: OTP Login successful, caching data');
    this.token = data.access_token;
    this.userCache = data.user;
    return data;
  }

  async register(userData: {
    display_name: string;
    email_address: string;
    password: string;
    time_zone?: string;
    locale?: string;
  }) {
    const response = await fetch(`${API_BASE_URL}/auth/register`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(userData),
    });

    const data = await this.handleResponse<{
      access_token: string;
      token_type: string;
      user: any;
    }>(response);

    this.token = data.access_token;
    this.userCache = data.user;
    localStorage.setItem('planner_token', data.access_token);
    localStorage.setItem('planner_user', JSON.stringify(data.user));

    return data;
  }

  async getCurrentUser() {
    // Return cached user data if available
    if (this.userCache) {
      console.log('API: Returning cached user data');
      return this.userCache;
    }

    console.log('API: No cached user data, fetching from server');
    const response = await fetch(`${API_BASE_URL}/auth/me`, {
      headers: this.getHeaders(),
    });

    const userData = await this.handleResponse<any>(response);

    // Cache the user data
    this.userCache = userData;
    localStorage.setItem('planner_user', JSON.stringify(userData));

    return userData;
  }

  async refreshToken(): Promise<boolean> {
    if (!this.token) return false;

    try {
      console.log('API: Refreshing access token');
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        console.warn('API: Token refresh failed with status:', response.status);
        if (response.status === 401) {
          this.token = null;
          this.userCache = null;
          localStorage.removeItem('planner_token');
          localStorage.removeItem('planner_user');
          localStorage.removeItem('planner_projects');
          localStorage.removeItem('planner_issues');
          localStorage.removeItem('planner_sprints');
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
        return false;
      }

      const data = await response.json();
      this.token = data.access_token;
      this.userCache = data.user;
      localStorage.setItem('planner_token', data.access_token);
      localStorage.setItem('planner_user', JSON.stringify(data.user));
      console.log('API: Token refreshed successfully');
      return true;
    } catch (error) {
      console.warn('API: Token refresh failed:', error);
      return false;
    }
  }

  async logout() {
    console.log('API: Logging out and clearing cache');
    try {
      await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        headers: this.getHeaders(),
      });
    } catch (error) {
      console.warn('Logout request failed:', error);
    }

    this.token = null;
    this.userCache = null;

    // Clear all cached data
    localStorage.removeItem('planner_token');
    localStorage.removeItem('planner_user');
    localStorage.removeItem('planner_projects');
    localStorage.removeItem('planner_issues');
    localStorage.removeItem('planner_sprints');

    console.log('API: All cached data cleared');
  }

  // Users
  async getUsers() {
    const cachedUsers = localStorage.getItem('planner_users');
    if (cachedUsers) {
      try {
        const users = JSON.parse(cachedUsers);
        console.log('API: Returning cached users data');
        return users[0] && users[0].displayName
          ? users
          : users.map((u: any) => this.transformUserSummary(u));
      } catch (error) {
        console.error('Failed to parse cached users:', error);
        localStorage.removeItem('planner_users');
      }
    }

    console.log('API: Fetching users from server');
    const response = await fetch(`${API_BASE_URL}/users`, {
      headers: this.getHeaders(),
    });

    const users = await this.handleResponse<any[]>(response);
    const normalized = users.map(u => this.transformUserSummary(u));
    localStorage.setItem('planner_users', JSON.stringify(normalized));
    return normalized;
  }

  // Projects
  async getProjects() {
    // Check cache first
    const cachedProjects = localStorage.getItem('planner_projects');
    if (cachedProjects) {
      try {
        const projects = JSON.parse(cachedProjects);
        console.log('API: Returning cached projects data');
        return projects;
      } catch (error) {
        console.error('Failed to parse cached projects:', error);
        localStorage.removeItem('planner_projects');
      }
    }

    console.log('API: Fetching projects from server');
    const response = await fetch(`${API_BASE_URL}/projects`, {
      headers: this.getHeaders(),
    });

    const projects = await this.handleResponse<any[]>(response);

    // Cache the projects data
    localStorage.setItem('planner_projects', JSON.stringify(projects));
    console.log('API: Projects data cached');

    return projects;
  }

  async createProject(projectData: any) {
    console.log('API: Creating new project');
    const response = await fetch(`${API_BASE_URL}/projects`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(projectData),
    });

    const newProject = await this.handleResponse<any>(response);

    // Update cached projects
    const cachedProjects = localStorage.getItem('planner_projects');
    if (cachedProjects) {
      try {
        const projects = JSON.parse(cachedProjects);
        projects.push(newProject);
        localStorage.setItem('planner_projects', JSON.stringify(projects));
        console.log('API: Updated cached projects with new project');
      } catch (error) {
        console.error('Failed to update cached projects:', error);
        localStorage.removeItem('planner_projects');
      }
    }

    return newProject;
  }

  // Chat
  private transformUserSummary(data: any): User {
    return {
      id: data.id,
      accountId: data.account_id || '',
      displayName: data.display_name,
      emailAddress: data.email_address,
      avatarUrl: data.avatar_url,
      active: data.active,
      timeZone: data.time_zone || 'UTC',
      locale: data.locale || 'en_US',
      groups: data.groups || [],
    };
  }

  private transformChatMessage(data: any): ChatMessage {
    return {
      id: data.id,
      chatId: data.chat_id,
      userId: data.user_id,
      user: data.user ? this.transformUserSummary(data.user) : undefined,
      content: data.content,
      timestamp: data.created_at,
      type: data.message_type,
      fileUrl: data.file_url || undefined,
      fileName: data.file_name,
      fileSize: data.file_size,
      mentions: data.mentions,
      replyTo: data.reply_to,
      edited: data.edited,
      editedAt: data.edited_at,
    };
  }

  private transformAttachment(data: any): Attachment {
    return {
      id: data.id,
      filename: data.filename,
      size: data.file_size,
      mimeType: data.mime_type,
      created: data.created_at,
      author: data.author_id,
      url: data.file_url || '',
    };
  }

  async getChatMessages(issueId: string): Promise<ChatMessage[]> {
    const response = await fetch(`${API_BASE_URL}/issues/${issueId}/chat/messages`, {
      headers: this.getHeaders(),
    });
    const data = await this.handleResponse<any[]>(response);
    return data.map(m => this.transformChatMessage(m));
  }

  async uploadChatFile(issueId: string, file: File): Promise<{
    file_url: string;
    file_name: string;
    file_size: number;
    message_type: string;
    view_url: string;
  }> {
    const formData = new FormData();
    formData.append('file', file);
    const headers: HeadersInit = {};
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }
    const response = await fetch(`${API_BASE_URL}/issues/${issueId}/chat/upload`, {
      method: 'POST',
      headers,
      body: formData,
    });
    return this.handleResponse(response);
  }

  getFileViewUrl(fileUrl?: string, fileName?: string, download: boolean = false): string {
    if (!fileUrl) return '';
    if (fileUrl.startsWith('blob:') || fileUrl.startsWith('data:')) return fileUrl;
    
    let targetUrl = fileUrl.trim();
    // Safely unwrap if already a view URL to prevent nesting
    while (targetUrl.includes('files/view?url=')) {
      try {
        const queryPart = targetUrl.split('url=')[1].split('&')[0];
        targetUrl = decodeURIComponent(queryPart);
      } catch (e) {
        break;
      }
    }

    const encodedUrl = encodeURIComponent(targetUrl);
    const fnParam = fileName ? `&filename=${encodeURIComponent(fileName)}` : '';
    const dlParam = download ? '&download=true' : '';
    return `${API_BASE_URL}/files/view?url=${encodedUrl}${fnParam}${dlParam}`;
  }

  async sendChatMessage(issueId: string, payload: {
    content: string;
    mentions?: string[];
    fileUrl?: string;
    fileName?: string;
    fileSize?: number;
    messageType?: string;
  }): Promise<ChatMessage> {
    const bodyPayload = {
      content: payload.content || '',
      message_type: payload.messageType || (payload.fileUrl ? 'file' : 'text'),
      file_url: payload.fileUrl,
      file_name: payload.fileName,
      file_size: payload.fileSize,
      mentions: payload.mentions || [],
    };
    const response = await fetch(`${API_BASE_URL}/issues/${issueId}/chat/messages`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(bodyPayload),
    });
    const data = await this.handleResponse<any>(response);
    return this.transformChatMessage(data);
  }

  async getProject(projectId: string) {
    const response = await fetch(`${API_BASE_URL}/projects/${projectId}`, {
      headers: this.getHeaders(),
    });

    return this.handleResponse<any>(response);
  }

  async updateProject(projectId: string, updates: any) {
    console.log('API: Updating project:', projectId);
    const response = await fetch(`${API_BASE_URL}/projects/${projectId}`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify(updates),
    });

    const updatedProject = await this.handleResponse<any>(response);

    // Update cached projects
    const cachedProjects = localStorage.getItem('planner_projects');
    if (cachedProjects) {
      try {
        const projects = JSON.parse(cachedProjects);
        const index = projects.findIndex((p: any) => p.id === projectId);
        if (index !== -1) {
          projects[index] = updatedProject;
          localStorage.setItem('planner_projects', JSON.stringify(projects));
          console.log('API: Updated cached project data');
        }
      } catch (error) {
        console.error('Failed to update cached projects:', error);
        localStorage.removeItem('planner_projects');
      }
    }

    return updatedProject;
  }

  async deleteProject(projectId: string) {
    console.log('API: Deleting project:', projectId);
    const response = await fetch(`${API_BASE_URL}/projects/${projectId}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });

    const result = await this.handleResponse<{ message: string }>(response);

    // Remove from cached projects
    const cachedProjects = localStorage.getItem('planner_projects');
    if (cachedProjects) {
      try {
        const projects = JSON.parse(cachedProjects);
        const filteredProjects = projects.filter((p: any) => p.id !== projectId);
        localStorage.setItem('planner_projects', JSON.stringify(filteredProjects));
        console.log('API: Removed project from cache');
      } catch (error) {
        console.error('Failed to update cached projects:', error);
        localStorage.removeItem('planner_projects');
      }
    }

    return result;
  }

  async getProjectStats(projectId: string) {
    const response = await fetch(`${API_BASE_URL}/projects/${projectId}/stats`, {
      headers: this.getHeaders(),
    });

    return this.handleResponse<any>(response);
  }

  // Issues
  async getIssues(params?: {
    project_id?: string;
    assignee?: string;
    status?: string;
    type?: string;
    priority?: string;
    fields?: string;
    limit?: number;
    skip?: number;
  }) {
    console.log('API: Fetching issues from server');
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, value.toString());
        }
      });
    }

    const response = await fetch(`${API_BASE_URL}/issues?${searchParams}`, {
      headers: this.getHeaders(),
    });

    const issues = await this.handleResponse<any[]>(response);

    return issues;
  }

  async getProjectIssues(projectId: string) {
    // Get all issues for the project without field restrictions
    return this.getIssues({
      project_id: projectId,
      limit: 10000
    });
  }

  async createIssue(issueData: any, files: File[] = []) {
    console.log('Creating new issue with data:', issueData);

    let body: BodyInit;
    let headers: HeadersInit;

    if (files.length > 0) {
      const formData = new FormData();
      formData.append('issue', JSON.stringify(issueData));
      files.forEach(file => formData.append('files', file));
      body = formData;
      headers = {};
      if (this.token) {
        headers['Authorization'] = `Bearer ${this.token}`;
      }
    } else {
      body = JSON.stringify(issueData);
      headers = this.getHeaders();
    }

    const response = await fetch(`${API_BASE_URL}/issues`, {
      method: 'POST',
      headers,
      body,
    });

    const result = await this.handleResponse<any>(response);
    console.log('Issue created successfully:', result);

    // Clear issues cache to force refresh
    this.clearIssuesCache();

    return {
      ...result,
      attachments: (result.attachments || []).map((a: any) => this.transformAttachment(a)),
    };
  }

  async createIssueWithAi(projectId: string, prompt: string, files: File[] = []) {
    console.log('Creating issue with AI for project:', projectId, 'with files:', files.length);
    let body: BodyInit;
    let headers: HeadersInit;

    if (files.length > 0) {
      const formData = new FormData();
      formData.append('payload', JSON.stringify({ project_id: projectId, prompt }));
      files.forEach(file => formData.append('files', file));
      body = formData;
      headers = {};
      if (this.token) {
        headers['Authorization'] = `Bearer ${this.token}`;
      }
    } else {
      body = JSON.stringify({ project_id: projectId, prompt });
      headers = this.getHeaders();
    }

    const response = await fetch(`${API_BASE_URL}/issues/ai-create`, {
      method: 'POST',
      headers,
      body,
    });
    const result = await this.handleResponse<any>(response);
    this.clearIssuesCache();
    return {
      ...result,
      attachments: (result.attachments || []).map((a: any) => this.transformAttachment(a)),
    };
  }

  // Helper method to clear issues cache
  private clearIssuesCache() {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('planner_issues_')) {
        localStorage.removeItem(key);
      }
    });
    console.log('API: Cleared issues cache');
  }
  // Email notification for issue changes
  async sendIssueNotification(
    issueId: string,
    assigneeIds: string[],
    updateType: 'assigned' | 'updated' = 'assigned'
  ) {
    try {
      const response = await fetch(`${API_BASE_URL}/cron/send-issue-notification`, {
        method: 'POST',
        headers: {
          ...this.getHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          issue_id: issueId,
          assignee_ids: assigneeIds,
          update_type: updateType,
        }),
      });

      if (!response.ok) {
        throw new Error(`Email service responded with ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to send issue notification:', error);
      throw error;
    }
  }

  async getNotifications(): Promise<Notification[]> {
    const response = await fetch(`${API_BASE_URL}/notifications`, {
      headers: this.getHeaders(),
    });
    const data = await this.handleResponse<any[]>(response);
    return data.map((n) => ({
      id: n.id,
      userId: n.user_id,
      title: n.title,
      message: n.message,
      type: n.type,
      read: n.read,
      actionUrl: n.action_url,
      createdAt: n.created_at,
    }));
  }

  async markNotificationRead(id: string): Promise<void> {
    await fetch(`${API_BASE_URL}/notifications/${id}/read`, {
      method: 'POST',
      headers: this.getHeaders(),
    });
  }

  // Get user details
  async getUser(userId: string) {
    // Check if it's the current user and return cached data
    if (this.userCache && this.userCache.id === userId) {
      console.log('API: Returning cached current user data');
      return this.userCache;
    }

    console.log('API: Fetching user data from server for:', userId);
    const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
      headers: this.getHeaders(),
    });

    return this.handleResponse<any>(response);
  }

  async getIssue(issueId: string) {
    console.log('API: Fetching issue details for:', issueId);
    const response = await fetch(`${API_BASE_URL}/issues/${issueId}`, {
      headers: this.getHeaders(),
    });
    const data = await this.handleResponse<any>(response);
    return {
      ...data,
      attachments: (data.attachments || []).map((a: any) => this.transformAttachment(a)),
    };
  }

  async sendSlackReminder(issueId: string) {
    console.log('API: Sending Slack reminder for issue:', issueId);
    const response = await fetch(`${API_BASE_URL}/issues/${issueId}/remind-slack`, {
      method: 'POST',
      headers: this.getHeaders(),
    });
    return this.handleResponse<any>(response);
  }

  async updateIssue(issueId: string, updates: any) {
    console.log('Updating issue:', issueId, 'with data:', updates);
    const response = await fetch(`${API_BASE_URL}/issues/${issueId}`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify(updates),
    });

    const result = await this.handleResponse<any>(response);
    console.log('Issue updated successfully:', result);

    // Clear issues cache to force refresh
    this.clearIssuesCache();

    return result;
  }

  async uploadIssueAttachments(issueId: string, files: File[]) {
    if (files.length === 0) return [];

    console.log('Uploading', files.length, 'attachments for issue:', issueId);
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));

    const headers: Record<string, string> = {};
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE_URL}/issues/${issueId}/attachments`, {
      method: 'POST',
      headers,
      body: formData,
    });

    const result = await this.handleResponse<any[]>(response);
    console.log('Attachments uploaded successfully:', result);

    // Clear issues cache to force refresh
    this.clearIssuesCache();

    return (result || []).map((a: any) => this.transformAttachment(a));
  }

  async deleteIssue(issueId: string) {
    console.log('API: Deleting issue:', issueId);
    const response = await fetch(`${API_BASE_URL}/issues/${issueId}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });

    const result = await this.handleResponse<{ message: string }>(response);

    // Clear issues cache to force refresh
    this.clearIssuesCache();

    return result;
  }

  // ─── SubTask Timeline API ──────────────────────────────────────────────────

  async getSubTasks(issueId: string) {
    try {
      const response = await fetch(`${API_BASE_URL}/issues/${issueId}/subtasks`, {
        headers: this.getHeaders(),
      });
      if (!response.ok) {
        // Silently return empty array — subtask section will be hidden
        return [];
      }
      return await response.json() as any[];
    } catch {
      return [];
    }
  }

  async createSubTask(issueId: string, data: {
    title: string;
    description?: string;
    status?: string;
    priority?: string;
    assignee_id?: string;
    start_date?: string;
    end_date?: string;
    progress?: number;
    color?: string;
    order_index?: number;
  }) {
    const response = await fetch(`${API_BASE_URL}/issues/${issueId}/subtasks`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });
    return this.handleResponse<any>(response);
  }

  async updateSubTask(issueId: string, subtaskId: string, data: {
    title?: string;
    description?: string;
    status?: string;
    priority?: string;
    assignee_id?: string;
    start_date?: string;
    end_date?: string;
    progress?: number;
    color?: string;
    order_index?: number;
  }) {
    const response = await fetch(`${API_BASE_URL}/issues/${issueId}/subtasks/${subtaskId}`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });
    return this.handleResponse<any>(response);
  }

  async deleteSubTask(issueId: string, subtaskId: string) {
    const response = await fetch(`${API_BASE_URL}/issues/${issueId}/subtasks/${subtaskId}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    return this.handleResponse<{ message: string }>(response);
  }

  async searchIssues(query: string, projectId?: string) {
    console.log('API: Searching issues with query:', query);
    const searchParams = new URLSearchParams({ q: query });
    if (projectId) {
      searchParams.append('project_id', projectId);
    }

    const response = await fetch(`${API_BASE_URL}/issues/search?${searchParams}`, {
      headers: this.getHeaders(),
    });

    return this.handleResponse<any[]>(response);
  }

  async bulkUpdateIssues(issueIds: string[], updates: any) {
    console.log('API: Bulk updating issues:', issueIds);
    const response = await fetch(`${API_BASE_URL}/issues/bulk-update`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({
        issue_ids: issueIds,
        updates,
      }),
    });

    const result = await this.handleResponse<any>(response);

    // Clear issues cache to force refresh
    this.clearIssuesCache();

    return result;
  }

  // Sprints
  async getSprints(params?: {
    project_id?: string;
    state?: string;
    skip?: number;
    limit?: number;
  }) {
    // Create cache key based on parameters
    const cacheKey = `planner_sprints_${JSON.stringify(params || {})}`;
    const cachedSprints = localStorage.getItem(cacheKey);

    if (cachedSprints) {
      try {
        const sprints = JSON.parse(cachedSprints);
        console.log('API: Returning cached sprints data');
        return sprints;
      } catch (error) {
        console.error('Failed to parse cached sprints:', error);
        localStorage.removeItem(cacheKey);
      }
    }

    console.log('API: Fetching sprints from server');
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, value.toString());
        }
      });
    }

    const response = await fetch(`${API_BASE_URL}/sprints?${searchParams}`, {
      headers: this.getHeaders(),
    });

    const sprints = await this.handleResponse<any[]>(response);

    // Cache the sprints data
    localStorage.setItem(cacheKey, JSON.stringify(sprints));
    console.log('API: Sprints data cached');

    return sprints;
  }

  // Helper method to clear sprints cache
  private clearSprintsCache() {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('planner_sprints_')) {
        localStorage.removeItem(key);
      }
    });
    console.log('API: Cleared sprints cache');
  }
  async createSprint(sprintData: any) {
    console.log('API: Creating new sprint');
    const response = await fetch(`${API_BASE_URL}/sprints`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(sprintData),
    });

    const newSprint = await this.handleResponse<any>(response);

    // Clear sprints cache to force refresh
    this.clearSprintsCache();

    return newSprint;
  }

  async getSprint(sprintId: string) {
    const response = await fetch(`${API_BASE_URL}/sprints/${sprintId}`, {
      headers: this.getHeaders(),
    });

    return this.handleResponse<any>(response);
  }

  async updateSprint(sprintId: string, updates: any) {
    console.log('API: Updating sprint:', sprintId);
    const response = await fetch(`${API_BASE_URL}/sprints/${sprintId}`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify(updates),
    });

    const result = await this.handleResponse<any>(response);

    // Clear sprints cache to force refresh
    this.clearSprintsCache();

    return result;
  }

  async startSprint(sprintId: string) {
    console.log('API: Starting sprint:', sprintId);
    const response = await fetch(`${API_BASE_URL}/sprints/${sprintId}/start`, {
      method: 'PUT',
      headers: this.getHeaders(),
    });

    const result = await this.handleResponse<{ message: string }>(response);

    // Clear sprints cache to force refresh
    this.clearSprintsCache();

    return result;
  }

  async completeSprint(sprintId: string) {
    console.log('API: Completing sprint:', sprintId);
    const response = await fetch(`${API_BASE_URL}/sprints/${sprintId}/complete`, {
      method: 'PUT',
      headers: this.getHeaders(),
    });

    const result = await this.handleResponse<{ message: string }>(response);

    // Clear sprints cache to force refresh
    this.clearSprintsCache();

    return result;
  }

  async getSprintStats(sprintId: string) {
    const response = await fetch(`${API_BASE_URL}/sprints/${sprintId}/stats`, {
      headers: this.getHeaders(),
    });

    return this.handleResponse<any>(response);
  }

  async addIssueToSprint(sprintId: string, issueId: string) {
    console.log('API: Adding issue to sprint:', { sprintId, issueId });
    const response = await fetch(`${API_BASE_URL}/sprints/${sprintId}/issues/${issueId}`, {
      method: 'POST',
      headers: this.getHeaders(),
    });

    const result = await this.handleResponse<{ message: string }>(response);

    // Clear both issues and sprints cache
    this.clearIssuesCache();
    this.clearSprintsCache();

    return result;
  }

  async removeIssueFromSprint(sprintId: string, issueId: string) {
    console.log('API: Removing issue from sprint:', { sprintId, issueId });
    const response = await fetch(`${API_BASE_URL}/sprints/${sprintId}/issues/${issueId}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });

    const result = await this.handleResponse<{ message: string }>(response);

    // Clear both issues and sprints cache
    this.clearIssuesCache();
    this.clearSprintsCache();

    return result;
  }

  // Dashboard
  async getDashboardStats() {
    console.log('API: Fetching dashboard stats');
    const response = await fetch(`${API_BASE_URL}/dashboard/stats`, {
      headers: this.getHeaders(),
    });

    return this.handleResponse<any>(response);
  }

  async getDashboardIssueBreakdown(filters: { year?: number; userId?: string; projectId?: string } = {}) {
    console.log('API: Fetching dashboard issue breakdown', filters);
    const params = new URLSearchParams();
    if (filters.year !== undefined) {
      params.set('year', String(filters.year));
    }
    if (filters.userId) {
      params.set('user_id', filters.userId);
    }
    if (filters.projectId) {
      params.set('project_id', filters.projectId);
    }

    const query = params.toString();
    const url = query
      ? `${API_BASE_URL}/dashboard/issue-breakdown?${query}`
      : `${API_BASE_URL}/dashboard/issue-breakdown`;

    const response = await fetch(url, {
      headers: this.getHeaders(),
    });

    return this.handleResponse<any>(response);
  }

  async getCompletionTrends(filters: { year?: number; month?: number; userId?: string; projectId?: string } = {}) {
    console.log('API: Fetching completion trends', filters);
    const params = new URLSearchParams();
    if (filters.year !== undefined) {
      params.set('year', String(filters.year));
    }
    if (filters.month !== undefined) {
      params.set('month', String(filters.month));
    }
    if (filters.userId) {
      params.set('user_id', filters.userId);
    }
    if (filters.projectId) {
      params.set('project_id', filters.projectId);
    }

    const query = params.toString();
    const url = query
      ? `${API_BASE_URL}/dashboard/completion-trends?${query}`
      : `${API_BASE_URL}/dashboard/completion-trends`;

    const response = await fetch(url, {
      headers: this.getHeaders(),
    });

    return this.handleResponse<any>(response);
  }

  async getRecentActivity(limit: number = 10) {
    const response = await fetch(`${API_BASE_URL}/dashboard/recent-activity?limit=${limit}`, {
      headers: this.getHeaders(),
    });

    return this.handleResponse<any>(response);
  }

  async getTeamPerformance() {
    const response = await fetch(`${API_BASE_URL}/dashboard/team-performance`, {
      headers: this.getHeaders(),
    });

    return this.handleResponse<any>(response);
  }

  async getProjectOverview() {
    const response = await fetch(`${API_BASE_URL}/dashboard/project-overview`, {
      headers: this.getHeaders(),
    });

    return this.handleResponse<any>(response);
  }

  async getProjectMembers(projectId: string): Promise<User[]> {
    console.log('API: Fetching project members for:', projectId);
    const response = await fetch(`${API_BASE_URL}/projects/${projectId}/members`, {
      headers: this.getHeaders(),
    });

    const members = await this.handleResponse<any[]>(response);
    return members.map(m => this.transformUserSummary(m));
  }

  async addProjectMember(projectId: string, email: string) {
    const response = await fetch(`${API_BASE_URL}/projects/${projectId}/members`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ email })
    });

    return this.handleResponse<any>(response);
  }

  async removeProjectMember(projectId: string, userId: string) {
    const response = await fetch(`${API_BASE_URL}/projects/${projectId}/members/${userId}`, {
      method: 'DELETE',
      headers: this.getHeaders()
    });

    return this.handleResponse<any>(response);
  }

  // Method to manually clear all cache (useful for debugging)
  clearAllCache() {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('planner_')) {
        localStorage.removeItem(key);
      }
    });
    this.userCache = null;
    console.log('API: All cache cleared manually');
  }

  // Method to refresh specific data (force API call)
  async refreshProjects() {
    localStorage.removeItem('planner_projects');
    return this.getProjects();
  }

  async refreshIssues(params?: any) {
    this.clearIssuesCache();
    return this.getIssues(params);
  }

  async refreshSprints(params?: any) {
    this.clearSprintsCache();
    return this.getSprints(params);
  }
  // Project Invitations
  async inviteUsersToProject(projectId: string, emails: string[], message?: string) {
    const response = await fetch(`${API_BASE_URL}/invitations/projects/${projectId}/invite`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({
        emails,
        message: message || ''
      }),
    });

    return this.handleResponse<any[]>(response);
  }

  async getProjectInvitations(projectId: string, status?: string) {
    const searchParams = new URLSearchParams();
    if (status) {
      searchParams.append('status', status);
    }

    const response = await fetch(`${API_BASE_URL}/invitations/projects/${projectId}/invitations?${searchParams}`, {
      headers: this.getHeaders(),
    });

    return this.handleResponse<any[]>(response);
  }

  async respondToInvitation(token: string, action: 'accept' | 'decline') {
    const response = await fetch(`${API_BASE_URL}/invitations/invitation/${token}/respond`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ action }),
    });

    return this.handleResponse<any>(response);
  }

  async getInvitationDetails(token: string) {
    const response = await fetch(`${API_BASE_URL}/invitations/invitation/${token}`, {
      headers: this.getHeaders(),
    });

    return this.handleResponse<any>(response);
  }

  async cancelInvitation(invitationId: string) {
    const response = await fetch(`${API_BASE_URL}/invitations/invitations/${invitationId}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });

    return this.handleResponse<any>(response);
  }

  async resendInvitation(invitationId: string) {
    const response = await fetch(`${API_BASE_URL}/invitations/invitations/${invitationId}/resend`, {
      method: 'POST',
      headers: this.getHeaders(),
    });

    return this.handleResponse<any>(response);
  }

  // Project Notes
  async getProjectNotes(projectId: string, params?: { pinned_only?: boolean }) {
    console.log('API: Fetching project notes for:', projectId);
    const searchParams = new URLSearchParams();
    if (params?.pinned_only) {
      searchParams.append('pinned_only', 'true');
    }

    const response = await fetch(`${API_BASE_URL}/notes/projects/${projectId}?${searchParams}`, {
      headers: this.getHeaders(),
    });

    return this.handleResponse<any[]>(response);
  }

  async createProjectNote(noteData: any) {
    console.log('API: Creating project note');
    const response = await fetch(`${API_BASE_URL}/notes`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(noteData),
    });

    return this.handleResponse<any>(response);
  }

  async getProjectNote(noteId: string) {
    const response = await fetch(`${API_BASE_URL}/notes/${noteId}`, {
      headers: this.getHeaders(),
    });

    return this.handleResponse<any>(response);
  }

  async updateProjectNote(noteId: string, updates: any) {
    console.log('API: Updating project note:', noteId);
    const response = await fetch(`${API_BASE_URL}/notes/${noteId}`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify(updates),
    });

    return this.handleResponse<any>(response);
  }

  async deleteProjectNote(noteId: string) {
    console.log('API: Deleting project note:', noteId);
    const response = await fetch(`${API_BASE_URL}/notes/${noteId}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });

    return this.handleResponse<any>(response);
  }

  async toggleNotePin(noteId: string) {
    console.log('API: Toggling note pin:', noteId);
    const response = await fetch(`${API_BASE_URL}/notes/${noteId}/pin`, {
      method: 'POST',
      headers: this.getHeaders(),
    });

    return this.handleResponse<any>(response);
  }
  // ─── Manager Dashboard APIs ─────────────────────────────────────────────

  async getManagerOverview(projectId?: string) {
    const params = new URLSearchParams();
    if (projectId) params.set('project_id', projectId);
    const query = params.toString();
    const url = query ? `${API_BASE_URL}/manager/overview?${query}` : `${API_BASE_URL}/manager/overview`;
    const response = await fetch(url, { headers: this.getHeaders() });
    return this.handleResponse<any>(response);
  }

  async getManagerProjects() {
    const response = await fetch(`${API_BASE_URL}/manager/projects`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse<any>(response);
  }

  async getManagerTeam(projectId?: string) {
    const params = new URLSearchParams();
    if (projectId) params.set('project_id', projectId);
    const query = params.toString();
    const url = query ? `${API_BASE_URL}/manager/team?${query}` : `${API_BASE_URL}/manager/team`;
    const response = await fetch(url, { headers: this.getHeaders() });
    return this.handleResponse<any>(response);
  }

  async getManagerTasks(filters: {
    projectId?: string;
    status?: string;
    priority?: string;
    assigneeId?: string;
    sortBy?: string;
    sortOrder?: string;
    page?: number;
    pageSize?: number;
  } = {}) {
    const params = new URLSearchParams();
    if (filters.projectId) params.set('project_id', filters.projectId);
    if (filters.status) params.set('status', filters.status);
    if (filters.priority) params.set('priority', filters.priority);
    if (filters.assigneeId) params.set('assignee_id', filters.assigneeId);
    if (filters.sortBy) params.set('sort_by', filters.sortBy);
    if (filters.sortOrder) params.set('sort_order', filters.sortOrder);
    if (filters.page) params.set('page', String(filters.page));
    if (filters.pageSize) params.set('page_size', String(filters.pageSize));
    const query = params.toString();
    const url = query ? `${API_BASE_URL}/manager/tasks?${query}` : `${API_BASE_URL}/manager/tasks`;
    const response = await fetch(url, { headers: this.getHeaders() });
    return this.handleResponse<any>(response);
  }

  async getManagerMonthlyTrends(projectId?: string, months: number = 12) {
    const params = new URLSearchParams();
    if (projectId) params.set('project_id', projectId);
    params.set('months', String(months));
    const response = await fetch(`${API_BASE_URL}/manager/trends/monthly?${params.toString()}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse<any>(response);
  }

  async getManagerWeeklyTrends(projectId?: string, weeks: number = 12) {
    const params = new URLSearchParams();
    if (projectId) params.set('project_id', projectId);
    params.set('weeks', String(weeks));
    const response = await fetch(`${API_BASE_URL}/manager/trends/weekly?${params.toString()}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse<any>(response);
  }

  async getManagerProjectDetail(projectId: string) {
    const response = await fetch(`${API_BASE_URL}/manager/projects/${projectId}/detail`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse<any>(response);
  }

  async getSprintBoardData(projectId?: string) {
    const params = new URLSearchParams();
    if (projectId) params.set('project_id', projectId);
    const query = params.toString();
    const url = query ? `${API_BASE_URL}/manager/sprint-board?${query}` : `${API_BASE_URL}/manager/sprint-board`;
    const response = await fetch(url, { headers: this.getHeaders() });
    return this.handleResponse<any>(response);
  }

  // ---------------------------------------------------------------------
  // Deployments
  // Deliberately uncached: deployment status is live data, and a stale
  // localStorage copy would show a finished deploy as still running.
  // ---------------------------------------------------------------------
  async getDeployments(params?: {
    project_id?: string;
    environment?: string;
    status?: string;
    source?: string;
    days?: number;
    skip?: number;
    limit?: number;
  }) {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          searchParams.append(key, value.toString());
        }
      });
    }
    const query = searchParams.toString();
    const response = await fetch(`${API_BASE_URL}/deployments${query ? `?${query}` : ''}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse<any[]>(response);
  }

  async getDeploymentStats(params?: { project_id?: string; environment?: string; days?: number }) {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          searchParams.append(key, value.toString());
        }
      });
    }
    const query = searchParams.toString();
    const response = await fetch(`${API_BASE_URL}/deployments/stats${query ? `?${query}` : ''}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse<any>(response);
  }

  async getDeploymentEnvironments(projectId?: string) {
    const query = projectId ? `?project_id=${encodeURIComponent(projectId)}` : '';
    const response = await fetch(`${API_BASE_URL}/deployments/environments${query}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse<any[]>(response);
  }

  async getDeployment(deploymentId: string) {
    const response = await fetch(`${API_BASE_URL}/deployments/${deploymentId}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse<any>(response);
  }

  async createDeployment(data: any) {
    const response = await fetch(`${API_BASE_URL}/deployments`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });
    return this.handleResponse<any>(response);
  }

  async updateDeployment(deploymentId: string, data: any) {
    const response = await fetch(`${API_BASE_URL}/deployments/${deploymentId}`, {
      method: 'PATCH',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });
    return this.handleResponse<any>(response);
  }

  async deleteDeployment(deploymentId: string) {
    const response = await fetch(`${API_BASE_URL}/deployments/${deploymentId}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    return this.handleResponse<any>(response);
  }

  // ---------------------------------------------------------------------
  // Sheets - user-defined tables with row-level comments.
  // Uncached: several people edit the same grid, so a stale localStorage copy
  // would silently show one user's view over another's edits.
  // ---------------------------------------------------------------------
  async getSheets(params?: { project_id?: string }) {
    const query = params?.project_id ? `?project_id=${encodeURIComponent(params.project_id)}` : '';
    const response = await fetch(`${API_BASE_URL}/sheets${query}`, { headers: this.getHeaders() });
    return this.handleResponse<any[]>(response);
  }

  async createSheet(data: { project_id: string; name: string; description?: string; initial_rows?: number }) {
    const response = await fetch(`${API_BASE_URL}/sheets`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });
    return this.handleResponse<any>(response);
  }

  async getSheet(sheetId: string) {
    const response = await fetch(`${API_BASE_URL}/sheets/${sheetId}`, { headers: this.getHeaders() });
    return this.handleResponse<any>(response);
  }

  async updateSheet(sheetId: string, data: any) {
    const response = await fetch(`${API_BASE_URL}/sheets/${sheetId}`, {
      method: 'PATCH',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });
    return this.handleResponse<any>(response);
  }

  async deleteSheet(sheetId: string) {
    const response = await fetch(`${API_BASE_URL}/sheets/${sheetId}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    return this.handleResponse<any>(response);
  }

  async addSheetColumn(sheetId: string, data: any) {
    const response = await fetch(`${API_BASE_URL}/sheets/${sheetId}/columns`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });
    return this.handleResponse<any>(response);
  }

  async updateSheetColumn(sheetId: string, columnId: string, data: any) {
    const response = await fetch(`${API_BASE_URL}/sheets/${sheetId}/columns/${columnId}`, {
      method: 'PATCH',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });
    return this.handleResponse<any>(response);
  }

  async deleteSheetColumn(sheetId: string, columnId: string) {
    const response = await fetch(`${API_BASE_URL}/sheets/${sheetId}/columns/${columnId}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    return this.handleResponse<any>(response);
  }

  async addSheetRow(sheetId: string, data: any = {}) {
    const response = await fetch(`${API_BASE_URL}/sheets/${sheetId}/rows`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ data }),
    });
    return this.handleResponse<any>(response);
  }

  async updateSheetRow(sheetId: string, rowId: string, body: any) {
    const response = await fetch(`${API_BASE_URL}/sheets/${sheetId}/rows/${rowId}`, {
      method: 'PATCH',
      headers: this.getHeaders(),
      body: JSON.stringify(body),
    });
    return this.handleResponse<any>(response);
  }

  async deleteSheetRow(sheetId: string, rowId: string) {
    const response = await fetch(`${API_BASE_URL}/sheets/${sheetId}/rows/${rowId}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    return this.handleResponse<any>(response);
  }

  async bulkWriteSheetRows(sheetId: string, body: { start_index: number; rows: Record<string, any>[] }) {
    const response = await fetch(`${API_BASE_URL}/sheets/${sheetId}/rows/bulk`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(body),
    });
    return this.handleResponse<any[]>(response);
  }

  async getSheetRowComments(sheetId: string, rowId: string) {
    const response = await fetch(`${API_BASE_URL}/sheets/${sheetId}/rows/${rowId}/comments`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse<any[]>(response);
  }

  async addSheetRowComment(sheetId: string, rowId: string, body: string) {
    const response = await fetch(`${API_BASE_URL}/sheets/${sheetId}/rows/${rowId}/comments`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ body }),
    });
    return this.handleResponse<any>(response);
  }

  async updateSheetComment(sheetId: string, commentId: string, data: any) {
    const response = await fetch(`${API_BASE_URL}/sheets/${sheetId}/comments/${commentId}`, {
      method: 'PATCH',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });
    return this.handleResponse<any>(response);
  }

  async deleteSheetComment(sheetId: string, commentId: string) {
    const response = await fetch(`${API_BASE_URL}/sheets/${sheetId}/comments/${commentId}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    return this.handleResponse<any>(response);
  }

  /** Real .xlsx download (typed cells, frozen header, comments tab). */
  async exportSheetXlsx(sheetId: string): Promise<{ blob: Blob; filename: string }> {
    const response = await fetch(`${API_BASE_URL}/sheets/${sheetId}/export.xlsx`, { headers: this.getHeaders() });
    if (!response.ok) {
      const detail = await response.json().catch(() => ({ detail: 'Export failed' }));
      throw new Error(typeof detail.detail === 'string' ? detail.detail : 'Export failed');
    }
    const disposition = response.headers.get('content-disposition') || '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    return { blob: await response.blob(), filename: match ? match[1] : 'sheet.xlsx' };
  }

  /** Upload a real Excel/CSV file into a sheet. Multipart, so the JSON
   *  Content-Type from getHeaders() must NOT be sent - the browser has to set
   *  its own multipart boundary. */
  async importSheetFile(sheetId: string, file: File, mode: 'append' | 'replace' = 'append') {
    const form = new FormData();
    form.append('file', file);
    form.append('mode', mode);
    form.append('create_missing_columns', 'true');

    const headers: Record<string, string> = {};
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`;

    const response = await fetch(`${API_BASE_URL}/sheets/${sheetId}/import`, {
      method: 'POST',
      headers,
      body: form,
    });
    return this.handleResponse<any>(response);
  }

  /** CSV download. Returns the blob plus the filename the server suggested. */
  async exportSheet(sheetId: string): Promise<{ blob: Blob; filename: string }> {
    const response = await fetch(`${API_BASE_URL}/sheets/${sheetId}/export`, { headers: this.getHeaders() });
    if (!response.ok) {
      const detail = await response.json().catch(() => ({ detail: 'Export failed' }));
      throw new Error(typeof detail.detail === 'string' ? detail.detail : 'Export failed');
    }
    const disposition = response.headers.get('content-disposition') || '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    return { blob: await response.blob(), filename: match ? match[1] : 'sheet.csv' };
  }

  // Health check
  async healthCheck() {
    const response = await fetch(`${API_BASE_URL}/health`);
    return this.handleResponse<any>(response);
  }
}

export const apiService = new ApiService();
